/**
 * Scrambl's navigation and module glyphs: one monochrome set drawn in the
 * macOS / ChatGPT manner (one idea per glyph, no accent colour).
 *
 * Grid: 32×32, content inside 4..28, 2-unit round strokes (1.25px at the
 * 20px rail size, the SF Symbols "regular" weight). Every glyph is data, and
 * both weights come from the same geometry:
 *
 *   body    closed silhouettes. Outline: stroked. Fill: filled + stroked, so
 *           the solid shape keeps the outline's outer edge. An entry can be
 *           `{ outline, fill }` when the outline needs a gap the solid
 *           shape doesn't (a clip sitting over a board's edge).
 *   detail  interior marks. Outline: stroked. Fill: knocked out of the solid
 *           body (`cut` overrides the knockout shape, e.g. a door opening).
 *   extra   open strokes that stay strokes in both weights (handles, rings).
 *   dots    small solid circles in both weights (list bullets).
 *
 * Draw new glyphs here with the helpers below; `local-icon.tsx` renders them.
 */

export interface NavGlyph {
  readonly body?: readonly (
    | string
    | { readonly outline: string; readonly fill: string }
  )[]
  readonly detail?: readonly { readonly d: string; readonly cut?: string }[]
  readonly extra?: readonly string[]
  readonly dots?: readonly (readonly [cx: number, cy: number, r: number])[]
}

const n = (v: number) => (Math.round(v * 100) / 100).toString()

/** Rounded rectangle, clockwise. */
const rr = (x: number, y: number, w: number, h: number, r = 3) =>
  `M${n(x + r)} ${n(y)}h${n(w - 2 * r)}a${n(r)} ${n(r)} 0 0 1 ${n(r)} ${n(r)}v${n(h - 2 * r)}a${n(r)} ${n(r)} 0 0 1 -${n(r)} ${n(r)}h-${n(w - 2 * r)}a${n(r)} ${n(r)} 0 0 1 -${n(r)} -${n(r)}v-${n(h - 2 * r)}a${n(r)} ${n(r)} 0 0 1 ${n(r)} -${n(r)}Z`

/** Circle as a path. */
const circ = (cx: number, cy: number, r: number) =>
  `M${n(cx - r)} ${n(cy)}a${n(r)} ${n(r)} 0 1 1 ${n(2 * r)} 0a${n(r)} ${n(r)} 0 1 1 -${n(2 * r)} 0Z`

/** Closed polygon from polar points [radius, degrees] around (cx, cy). */
const polar = (cx: number, cy: number, pts: [number, number][]) =>
  pts
    .map(([r, deg], i) => {
      const a = ((deg - 90) * Math.PI) / 180
      return `${i ? 'L' : 'M'}${n(cx + r * Math.cos(a))} ${n(cy + r * Math.sin(a))}`
    })
    .join('') + 'Z'

/** Gear: `teeth` flat-topped teeth between an outer and inner radius. */
const gear = (
  cx: number,
  cy: number,
  outer: number,
  inner: number,
  teeth = 8,
) => {
  const step = 360 / teeth
  const pts: [number, number][] = []
  for (let i = 0; i < teeth; i++) {
    const a = i * step
    pts.push(
      [inner, a - step * 0.32],
      [outer, a - step * 0.17],
      [outer, a + step * 0.17],
      [inner, a + step * 0.32],
    )
  }
  return polar(cx, cy, pts)
}

const page = 'M19 5H9a2 2 0 0 0-2 2v18a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V11Z'
const pageFold = 'M19 5v6h6'

export const NAV_GLYPHS = {
  home: {
    body: ['M6 14 16 6l10 8v11a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2Z'],
    detail: [
      {
        d: 'M13 27v-5.5a1.5 1.5 0 0 1 1.5-1.5h3a1.5 1.5 0 0 1 1.5 1.5V27',
        cut: rr(13, 20, 6, 9, 1.5),
      },
    ],
  },
  users: {
    body: [circ(12, 11.5, 4.5), 'M4.5 26a7.5 7.5 0 0 1 15 0Z'],
    extra: [
      'M19.5 7.2a4.5 4.5 0 0 1 0 8.6',
      'M22.5 18.8A7.5 7.5 0 0 1 27.5 26',
    ],
  },
  user: {
    body: [circ(16, 11, 5), 'M6.5 27a9.5 9.5 0 0 1 19 0Z'],
  },
  userCircle: {
    body: [circ(16, 16, 11)],
    detail: [{ d: circ(16, 13.5, 4) }, { d: 'M9 23.5a8 8 0 0 1 14 0' }],
  },
  folder: {
    body: [
      'M5 9a2 2 0 0 1 2-2h5.5l3 3H25a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2Z',
    ],
  },
  checkSquare: {
    body: [rr(5, 5, 22, 22, 4)],
    detail: [{ d: 'M11 16.5l3.5 3.5 7-7.5' }],
  },
  ticket: {
    body: [
      'M5 9a2 2 0 0 1 2-2h18a2 2 0 0 1 2 2v3.5a3.5 3.5 0 0 0 0 7V23a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-3.5a3.5 3.5 0 0 0 0-7Z',
    ],
    detail: [{ d: 'M19.5 11v10' }],
  },
  clock: {
    body: [circ(16, 16, 11)],
    detail: [{ d: 'M16 10v6.5l4 2.5' }],
  },
  funnel: {
    body: ['M6 6.5h20l-7.5 9.5v8l-5 2.5V16Z'],
  },
  kanban: {
    body: [rr(5, 5, 22, 22, 3)],
    detail: [{ d: 'M11 10v12M16 10v7M21 10v9' }],
  },
  document: {
    body: [page],
    detail: [{ d: pageFold }, { d: 'M12 17h8M12 21h8' }],
  },
  contract: {
    body: [page],
    detail: [
      { d: pageFold },
      { d: 'M12 14.5h7' },
      { d: 'M11.5 22c1.4-3.2 3.3-3.2 3.8 0s2.4 3.2 3.8 0' },
    ],
  },
  receipt: {
    body: ['M7 5h18v22l-3-2-3 2-3-2-3 2-3-2-3 2Z'],
    detail: [{ d: 'M11.5 11h9M11.5 15.5h9M11.5 20h5' }],
  },
  chat: {
    body: [
      'M8 6h16a3 3 0 0 1 3 3v11a3 3 0 0 1-3 3H13l-5 4v-4a3 3 0 0 1-3-3V9a3 3 0 0 1 3-3Z',
    ],
  },
  grid: {
    body: [
      rr(5, 5, 9, 9, 2),
      rr(18, 5, 9, 9, 2),
      rr(5, 18, 9, 9, 2),
      rr(18, 18, 9, 9, 2),
    ],
  },
  layout: {
    body: [rr(5, 5, 22, 22, 3)],
    detail: [{ d: 'M5 12h22M13 12v15' }],
  },
  cube: {
    body: ['M16 4.5 26.5 10.5v11L16 27.5 5.5 21.5v-11Z'],
    detail: [{ d: 'M5.5 10.5 16 16.5l10.5-6M16 16.5v11' }],
  },
  megaphone: {
    body: ['M5 13a2 2 0 0 1 2-2h5l12-5.5v21L12 21H7a2 2 0 0 1-2-2Z'],
    detail: [{ d: 'M12 11v10' }],
    extra: ['M12.5 21l1.5 6'],
  },
  gear: {
    body: [gear(16, 16, 11.5, 9)],
    detail: [{ d: circ(16, 16, 3.5) }],
  },
  signOut: {
    extra: [
      'M13 27H8a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h5',
      'M21 10.5 26.5 16 21 21.5',
      'M26.5 16H13',
    ],
  },
  bell: {
    body: ['M8 14a8 8 0 0 1 16 0c0 4.5 1 7.5 2.5 9.5h-21C7 21.5 8 18.5 8 14Z'],
    extra: ['M13 27a3 3 0 0 0 6 0'],
  },
  buildings: {
    body: [
      rr(5, 5, 13, 22, 2),
      'M18 12.5h7a2 2 0 0 1 2 2V25a2 2 0 0 1-2 2h-7Z',
    ],
    detail: [{ d: 'M9.5 10.5h4M9.5 15.5h4M9.5 20.5h4' }],
  },
  tree: {
    body: [
      rr(12, 4.5, 8, 6, 1.5),
      rr(4.5, 21.5, 8, 6, 1.5),
      rr(19.5, 21.5, 8, 6, 1.5),
    ],
    extra: ['M16 10.5V16', 'M8.5 21.5V18a2 2 0 0 1 2-2h11a2 2 0 0 1 2 2v3.5'],
  },
  tag: {
    body: ['M5 6.5A1.5 1.5 0 0 1 6.5 5H15l12 12-10 10L5 15Z'],
    detail: [{ d: circ(11, 11, 2) }],
  },
  trendUp: {
    // The arrowhead turns solid when selected, the glyph's one fill area.
    body: [{ outline: 'M21 10h6v6', fill: 'M20.5 9.5H27.5V16.5Z' }],
    extra: ['M5 22l7-7 5 5 10-10'],
  },

  chartBar: {
    body: [
      rr(5.5, 16, 5, 11, 1.5),
      rr(13.5, 10, 5, 17, 1.5),
      rr(21.5, 5, 5, 22, 1.5),
    ],
  },
  envelope: {
    body: [rr(5, 7, 22, 18, 2.5)],
    detail: [{ d: 'M5.5 9.5 16 17l10.5-7.5' }],
  },
  note: {
    body: ['M7 5h18a2 2 0 0 1 2 2v12l-8 8H7a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2Z'],
    detail: [{ d: 'M27 19h-6a2 2 0 0 0-2 2v6' }],
  },
  calendar: {
    body: [rr(5, 7, 22, 20, 2.5)],
    detail: [{ d: 'M5 13h22' }],
    extra: ['M11 4.5v5', 'M21 4.5v5'],
  },
  flow: {
    body: [rr(5, 5, 8, 8, 2), rr(19, 19, 8, 8, 2)],
    extra: ['M13 9h5a3 3 0 0 1 3 3v7'],
  },
  list: {
    extra: ['M12 9h15', 'M12 16h15', 'M12 23h15'],
    dots: [
      [6.5, 9, 1.75],
      [6.5, 16, 1.75],
      [6.5, 23, 1.75],
    ],
  },
  clipboard: {
    body: [
      {
        outline:
          'M11 6.5H8.5A2.5 2.5 0 0 0 6 9v15.5A2.5 2.5 0 0 0 8.5 27h15a2.5 2.5 0 0 0 2.5-2.5V9a2.5 2.5 0 0 0-2.5-2.5H21',
        fill: rr(6, 6.5, 20, 20.5, 2.5),
      },
    ],
    detail: [{ d: 'M11 16h10M11 21h7' }],
    extra: [rr(11, 4.5, 10, 4.5, 1.5)],
  },
  pulse: {
    extra: ['M5 16h4.5l3-6 5 12 3-6H27'],
  },
  card: {
    body: [rr(5, 8, 22, 16, 2.5)],
    detail: [{ d: 'M5 13h22M9.5 19h4' }],
  },
  wallet: {
    body: [rr(5, 8, 22, 18, 3)],
    detail: [{ d: 'M27 14h-5a3 3 0 0 0 0 6h5' }],
    extra: ['M8 8l11.5-3.3A1.5 1.5 0 0 1 21.5 6v2'],
  },
  search: {
    body: [circ(14.5, 14.5, 8.5)],
    extra: ['M20.5 20.5 26.5 26.5'],
  },
} as const satisfies Record<string, NavGlyph>

export type NavGlyphName = keyof typeof NAV_GLYPHS
